/**
 * SSE Intermediate Event Types
 *
 * These types define the intermediate event format used between SSE parsing
 * and the final AgentEvent transformation. SSE events from the server are
 * first transformed to this format, then to AgentEvent for the event bus.
 *
 * Note: Previously named "WebSocketEvent" but renamed for clarity since
 * this codebase uses SSE, not WebSockets.
 */

import type { AgentEvent } from './events/types.js';
import { generateEventId } from './events/utils.js';

export type SSEIntermediateEvent =
    | {
          type: 'connectionStatus';
          status: 'connected' | 'disconnected';
      }
    | {
          type: 'chunk';
          content: string;
          isComplete?: boolean;
          sessionId?: string;
          chunkType?: 'text' | 'reasoning';
      }
    | {
          type: 'response';
          text: string;
          sessionId?: string;
          reasoning?: string;
          tokenUsage?: {
              inputTokens?: number;
              outputTokens?: number;
              reasoningTokens?: number;
              totalTokens?: number;
          };
          model?: string;
          provider?: string;
          router?: string;
      }
    | { type: 'thinking'; sessionId?: string }
    | { type: 'conversationReset'; sessionId?: string }
    | {
          type: 'toolCall';
          toolName: string;
          toolArgs: Record<string, unknown>;
          toolCallId: string;
          sessionId?: string;
      }
    | {
          type: 'toolResult';
          toolCallId: string;
          toolName?: string;
          result: unknown;
          success: boolean;
          sessionId?: string;
      }
    | {
          type: 'approvalRequest';
          approvalId: string;
          approvalType: 'tool_confirmation' | 'command_confirmation' | 'elicitation';
          metadata: Record<string, unknown>;
          sessionId?: string;
      }
    | {
          type: 'approvalResolved';
          approvalId: string;
          status: 'approved' | 'denied' | 'cancelled';
          sessionId?: string;
      }
    | {
          type: 'mcpServerConnected';
          serverName: string;
          success: boolean;
          error?: string;
      }
    | {
          type: 'availableToolsUpdated';
          tools: unknown[];
          source: string;
      }
    | {
          type: 'mcpResourceUpdated';
          serverName?: string;
          resourceUri?: string;
      }
    | {
          type: 'mcpPromptsListChanged';
          serverName: string;
          prompts: unknown[];
      }
    | {
          type: 'mcpToolsListChanged';
          serverName: string;
          tools: unknown[];
      }
    | {
          type: 'resourceCacheInvalidated';
          resourceUri: string;
          serverName?: string;
          action?: string;
      }
    | {
          type: 'sessionTitleUpdated';
          sessionId: string;
          title: string;
      }
    | {
          type: 'taskCreated';
          task: any;
      }
    | {
          type: 'taskUpdated';
          task: any;
      }
    | {
          type: 'taskDeleted';
          taskId: string;
      }
    | {
          type: 'scheduler:scheduleCreated';
          schedule: any;
      }
    | {
          type: 'scheduler:scheduleUpdated';
          schedule: any;
      }
    | {
          type: 'scheduler:scheduleDeleted';
          scheduleId: string;
          scheduleName: string;
      }
    | {
          type: 'scheduler:scheduleTriggered';
          schedule: any;
          triggeredAt: number;
      }
    | {
          type: 'scheduler:executionCompleted';
          schedule: any;
          log: any;
      }
    | {
          type: 'scheduler:executionFailed';
          schedule: any;
          error: string;
          log: any;
      }
    | {
          type: 'trigger:created';
          taskId: string;
          sessionId: string;
          status: string;
          source: any;
      }
    | {
          type: 'trigger:processing';
          taskId: string;
          sessionId: string;
          status: string;
      }
    | {
          type: 'trigger:completed';
          taskId: string;
          sessionId: string;
          status: string;
          response?: {
              message: string;
              artifacts?: unknown[];
              metadata?: Record<string, unknown>;
          };
      }
    | {
          type: 'trigger:failed';
          taskId: string;
          sessionId: string;
          status: string;
          error: string;
      }
    | {
          type: 'trigger:pending';
          taskId: string;
          sessionId: string;
          status: string;
          reason: string;
      }
    | {
          type: 'trigger:approved';
          taskId: string;
          sessionId: string;
          status: string;
      }
    | {
          type: 'trigger:rejected';
          taskId: string;
          sessionId: string;
          status: string;
          reason?: string;
      }
    | {
          type: 'askUserResolved';
          requestId: string;
      }
    | {
          type: 'askUserPrompt';
          requestId: string;
          sessionId?: string;
          taskId?: string;
          title?: string;
          prompt: string;
          fields?: Array<{
              id: string;
              label: string;
              type: 'text' | 'textarea' | 'select' | 'boolean' | 'number';
              required?: boolean;
              placeholder?: string;
              helperText?: string;
              options?: Array<{ value: string; label: string }>;
              initialValue?: string | number | boolean;
          }>;
          schema?: Record<string, unknown>;
          metadata?: Record<string, unknown>;
          fromAgent?: string;
      }
    | { type: 'pong' }
    | { type: 'error'; error: string; sessionId?: string }
    | {
          type: 'uiAction';
          action: string;
          args: Record<string, unknown>;
          sessionId?: string;
      };

/**
 * Transform an SSEIntermediateEvent to an AgentEvent for the event bus
 * Adds required id and timestamp fields
 */
export function transformSSEIntermediateEvent(event: SSEIntermediateEvent): AgentEvent | null {
    const timestamp = Date.now();
    const baseId = generateEventId();

    switch (event.type) {
        case 'connectionStatus':
            return {
                type: 'connectionStatus',
                status: event.status,
                timestamp,
            };

        case 'thinking':
            return {
                type: 'thinking',
                id: baseId,
                timestamp,
                sessionId: event.sessionId,
            };

        case 'chunk':
            return {
                type: 'chunk',
                id: baseId,
                timestamp,
                content: event.content,
                chunkType: event.chunkType || 'text',
                isComplete: event.isComplete,
                sessionId: event.sessionId,
            };

        case 'response':
            return {
                type: 'response',
                id: baseId,
                timestamp,
                text: event.text,
                reasoning: event.reasoning,
                tokenUsage: event.tokenUsage,
                model: event.model,
                provider: event.provider,
                router: event.router,
                sessionId: event.sessionId,
            };

        case 'toolCall':
            return {
                type: 'toolCall',
                id: baseId,
                timestamp,
                toolName: event.toolName,
                toolArgs: event.toolArgs,
                toolCallId: event.toolCallId,
                sessionId: event.sessionId,
            };

        case 'toolResult':
            return {
                type: 'toolResult',
                id: baseId,
                timestamp,
                toolName: event.toolName || '',
                toolCallId: event.toolCallId,
                success: event.success,
                result: event.result,
                sessionId: event.sessionId,
            };

        case 'error':
            return {
                type: 'error',
                id: baseId,
                timestamp,
                error: event.error,
                sessionId: event.sessionId,
            };

        case 'approvalRequest':
            if (event.approvalType === 'elicitation') {
                return {
                    type: 'approvalRequest',
                    id: baseId,
                    timestamp,
                    approvalId: event.approvalId,
                    approvalType: 'elicitation',
                    metadata: event.metadata,
                    sessionId: event.sessionId,
                };
            } else if (event.approvalType === 'command_confirmation') {
                return {
                    type: 'approvalRequest',
                    id: baseId,
                    timestamp,
                    approvalId: event.approvalId,
                    approvalType: 'command_confirmation',
                    toolName: (event.metadata?.toolName as string) || '',
                    command: (event.metadata?.command as string) || '',
                    originalCommand: event.metadata?.originalCommand as string | undefined,
                    metadata: event.metadata,
                    sessionId: event.sessionId,
                };
            } else {
                return {
                    type: 'approvalRequest',
                    id: baseId,
                    timestamp,
                    approvalId: event.approvalId,
                    approvalType: 'tool_confirmation',
                    toolName: (event.metadata?.toolName as string) || '',
                    args: (event.metadata?.args as Record<string, unknown>) || {},
                    description: event.metadata?.description as string | undefined,
                    metadata: event.metadata,
                    sessionId: event.sessionId,
                };
            }

        case 'sessionTitleUpdated':
            return {
                type: 'sessionTitleUpdated',
                id: baseId,
                timestamp,
                sessionId: event.sessionId,
                title: event.title,
            };

        case 'conversationReset':
            return {
                type: 'conversationReset',
                id: baseId,
                timestamp,
                sessionId: event.sessionId,
            };

        case 'taskCreated':
            return {
                type: 'taskCreated',
                id: baseId,
                timestamp,
                task: event.task,
            };

        case 'taskUpdated':
            return {
                type: 'taskUpdated',
                id: baseId,
                timestamp,
                task: event.task,
            };

        case 'taskDeleted':
            return {
                type: 'taskDeleted',
                id: baseId,
                timestamp,
                taskId: event.taskId,
            };

        case 'scheduler:scheduleCreated':
            return {
                type: 'scheduler:scheduleCreated',
                id: baseId,
                timestamp,
                schedule: event.schedule,
            };

        case 'scheduler:scheduleUpdated':
            return {
                type: 'scheduler:scheduleUpdated',
                id: baseId,
                timestamp,
                schedule: event.schedule,
            };

        case 'scheduler:scheduleDeleted':
            return {
                type: 'scheduler:scheduleDeleted',
                id: baseId,
                timestamp,
                scheduleId: event.scheduleId,
                scheduleName: event.scheduleName,
            };

        case 'scheduler:scheduleTriggered':
            return {
                type: 'scheduler:scheduleTriggered',
                id: baseId,
                timestamp,
                schedule: event.schedule,
                triggeredAt: event.triggeredAt,
            };

        case 'scheduler:executionCompleted':
            return {
                type: 'scheduler:executionCompleted',
                id: baseId,
                timestamp,
                schedule: event.schedule,
                log: event.log,
            };

        case 'scheduler:executionFailed':
            return {
                type: 'scheduler:executionFailed',
                id: baseId,
                timestamp,
                schedule: event.schedule,
                error: event.error,
                log: event.log,
            };

        case 'approvalResolved':
            return {
                type: 'approvalResolved',
                id: baseId,
                timestamp,
                approvalId: event.approvalId,
                status: event.status,
                sessionId: event.sessionId,
            };

        case 'uiAction':
            return {
                type: 'uiAction',
                id: baseId,
                timestamp,
                action: event.action,
                args: event.args,
                sessionId: event.sessionId,
            };

        // Events handled elsewhere or don't need transformation
        case 'pong':
        case 'mcpServerConnected':
        case 'availableToolsUpdated':
        case 'mcpResourceUpdated':
        case 'mcpPromptsListChanged':
        case 'mcpToolsListChanged':
        case 'resourceCacheInvalidated':
        case 'askUserResolved':
        case 'askUserPrompt':
            return null;

        default:
            console.warn('[SSE] Unknown intermediate event type:', (event as any).type);
            return null;
    }
}
